import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

const FilterDropdown = ({
  options,
  disabled,
  selectedOptions,
  activeCalculation = false,
  withTransform = false,
  selectTitleClassname = "",
  selectInputClassname = "",
  onChange,
  tableData,
  tableRef,
  placeholder,
  renderOption,
  testId,
  disableScrollRecalculation = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [dropdownStyles, setDropdownStyles] = useState(null);

  const getValue = (option) => {
    if (option === null || option === undefined) return option;
    if (typeof option === 'string' || typeof option === 'number' || typeof option === 'boolean') {
      return option;
    }
    return option.value;
  };

  const isHeader = (option) => typeof option === 'object' && option.isHeader;

  const toggleOption = (option) => {
    const value = getValue(option);
    const newSelectedOptions = selectedOptions.includes(value)
      ? selectedOptions.filter(s => s !== value)
      : [...selectedOptions, value];
    onChange(newSelectedOptions);
  };

  useEffect(() => {
    if (!disableScrollRecalculation) {
      updateDropdownPosition()
    }
  }, [tableData, disableScrollRecalculation])

  const selectAll = (e) => {
    e.stopPropagation();
    const allValues = options
      .filter(o => !isHeader(o))
      .map(getValue);
    onChange(allValues);
  };

  const selectNone = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const selectOnly = (e, option) => {
    e.stopPropagation();
    const value = getValue(option);
    if (!isHeader(option)) {
      onChange([value]);
    }
  };

  const calculateDistanceToTop = () => {
    let distanceToTop = 0;
    let element = dropdownRef.current;
    while (element) {
      distanceToTop += element.offsetTop;
      element = element.offsetParent;
    }
    return distanceToTop;
  };
  
  const updateDropdownPosition = () => { 
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      const distanceToTheTopFromDropdown = calculateDistanceToTop();
      setDropdownStyles({
        top: (
          distanceToTheTopFromDropdown + dropdownRef.current.clientHeight
        ) - scrollY,
        left: rect.left + scrollX,
        ...(withTransform ? { transform: 'translateX(calc(-100% + 109px))' } : {})
      });
    }
  }

  const handleOpen = () => {
    setIsOpen(!isOpen);
  }

  useEffect(() => {
    if (testId) return
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    if (!disableScrollRecalculation) {
      if (tableRef?.current) {
        tableRef.current.addEventListener("scroll", updateDropdownPosition);
      }
      document.addEventListener("scroll", updateDropdownPosition);
      window.addEventListener("resize", updateDropdownPosition);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (!disableScrollRecalculation) {
        if (tableRef?.current) {
          tableRef.current.removeEventListener("scroll", updateDropdownPosition);
        }
        document.removeEventListener("scroll", updateDropdownPosition);
        window.removeEventListener("resize", updateDropdownPosition);
      }
    };
  }, [isOpen, tableRef, disableScrollRecalculation]);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef} data-testid={testId}>
      <div>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId ? `${testId}_open_filter` : undefined}
          className={`inline-flex justify-between items-center w-full disabled:bg-gray-300 rounded-md border border-gray-300 shadow-sm px-4 py-2.5 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 h-10 ${activeCalculation ? "focus:ring-primary" : "focus:ring-indigo-500"} ${selectInputClassname || ""}`}
          onClick={handleOpen}
        >
          <span className={selectTitleClassname}>{placeholder}</span>
          <ChevronDownIcon className="h-5 w-5 ml-2" aria-hidden="true" />
        </button>
      </div>

      <div style={dropdownStyles} className={`${isOpen ? "" : "hidden"} fixed mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50`}>
        <div className="py-1 bg-white rounded-md" role="menu" aria-orientation="vertical">
          <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-200 flex justify-center space-x-4 bg-white">
            <button 
              onClick={selectAll} 
              className={`${activeCalculation ? "text-primary" : "text-blue-600"} hover:underline`}
              data-testid={testId ? `${testId}_select_all` : undefined}
            >
              Select All
            </button>
            <button 
              onClick={selectNone} 
              className={`${activeCalculation ? "text-primary" : "text-blue-600"} hover:underline`}
              data-testid={testId ? `${testId}_select_none` : undefined}
            >
              Select None
            </button>
          </div>
          {options?.map((option, index) => {
            if (isHeader(option)) {
              return (
                <div
                  key={`header-${index}`}
                  className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-50"
                >
                  {option.label}
                </div>
              );
            }
            const value = getValue(option);
            const label = typeof option === 'string' ? option : option.label;
            return (
              <div
                key={value}
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 cursor-pointer bg-white"
              >
                <input
                  type="checkbox"
                  checked={selectedOptions.includes(value)}
                  data-testid={testId ? `${testId}_checkbox_${index}` : undefined}
                  onChange={() => toggleOption(option)}
                  className={`mr-2 ${activeCalculation ? "accent-primary" : "accent-blue-500"}`}
                />
                {renderOption ? renderOption(option) : (
                  <span className="text-gray-900">{label}</span>
                )}
                <button
                  onClick={(e) => selectOnly(e, option)}
                  className="ml-auto text-gray-400 hover:underline"
                >
                  only
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FilterDropdown;
